import React from "react";
import "./lead-conversion-rate.scss";
import { Box, Button } from "@mui/material";
import Select, { components } from "react-select";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import './lead-conversion-rate.scss'; 

interface LeadConversionRateProps {
  metricsData?: any;
  isLoading?: boolean;
  exportChart?: (params: any, data?: any) => void;
  enableExport?: boolean;
}

const LeadConversionRate: React.FC<LeadConversionRateProps> = ({
  metricsData,
  isLoading,
  exportChart,
  enableExport = false,
}) => {
  const [format, setFormat] = React.useState({ value: "PDF", label: "PDF" });
  const seriesData: any = [];
  const labels: any = [];
  const campaignsConversionRate = metricsData?.leadsConversionRate || [];
  campaignsConversionRate.forEach((campaign: any) => {
    seriesData.push(campaign.leadConversionRate.replace("%", ""));
    labels.push(campaign.name);
  });

  const series = [
    {
      name: "Lead Conversion Rate",
      data: seriesData.slice(0, 5),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: "line",

      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#7B8A8B",
        fontSize: "18px",
        fontFamily: "Roboto, sans-serif",
      },
    },
    stroke: {
      curve: "straight",
      width: 2,
    },

    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      formatter: function (val, opts) {
        const dataLength = opts.w.config.series[opts.seriesIndex].data.length;
        // Show label only on the last data point
        return opts.dataPointIndex === dataLength - 1 ? `${val}` : "";
      },
      // offsetY: -2,
      // offsetX: -5,
      style: {
        fontSize: "14px",
        fontWeight: 400,
        colors: ["#1C2F8D"],
        fontFamily: "Roboto, sans-serif",
      },
      background: {
        enabled: false,
      },
    },
    xaxis: {
      categories: labels.slice(0, 5) || [],
      axisTicks: {
        show: false,
      },
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
      },
      title: {
        text: "Campaigns",
        style: {
          fontSize: "14px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      title: {
        text: "Conversion Rate in (%)",
        style: {
          fontSize: "14px",
          fontWeight: 400,
          cssClass: "y-axis-title",
        },
        offsetX: -6,
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val}%`,
      },
    },
    colors: ["#556ee6"],
    grid: {
      borderColor: "#e0e0e0",
      padding: {
        right: 40, // try 40 or more until it fits
      },
    },
  };

  const fullSeriesData = [
    {
      name: "Lead Conversion Rate",
      data: seriesData,
    },
  ];
  const fullOptions: ApexOptions = {
    ...options,
    xaxis: {
      ...options.xaxis,
      categories: labels || [],
      labels: {
        style: {
          fontSize: "14px",
          colors: ["black"], // Optional: label color
        },
      },
    },
    chart: {
      type: "line",
      id: "leadsConversionRate",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
  };

  const handleExport = () => {
    if (!exportChart) return;

    const params = {
      metrics: "leadsConversionRate",
      format: format?.value?.toLowerCase(),
    };
    exportChart(params, format.value === "PDF" ? params?.metrics : undefined);
  };

  return (
    <Box className="lead-wrapper">
      <Box>
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="header-text">Lead Conversion Rate (%)</p>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Select
                options={[
                  { value: "PDF", label: "PDF" },
                  { value: "CSV", label: "CSV" },
                ]}
                isSearchable={false}
                value={format} // default selected value
                className="select-dropdown"
                onChange={(selectedOption: any) => setFormat(selectedOption)}
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: (props) => {
                    return (
                      <components.DropdownIndicator {...props}>
                        <ArrowDropDownIcon
                          style={{ fontSize: 20, color: "black" }}
                        />
                      </components.DropdownIndicator>
                    );
                  },
                }}
              />

              <Button
                variant="outlined"
                onClick={handleExport}
                className="address-edit-button"
                disabled={!enableExport}
              >
                Export
              </Button>
            </Box>
          </Box>

          {!isLoading && seriesData?.length ? (
            <div
              style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                width: "800px",
                height: "400px",
                overflow: "hidden",
              }}
            >
              <Chart
                options={fullOptions}
                series={fullSeriesData}
                type="line"
                height={250}
              />
            </div>
          ) : (
            <></>
          )}
          <Box sx={{ padding: "0px 20px" }}>
            {!isLoading && seriesData?.length ? (
              <Chart
                options={options}
                series={series}
                type="line"
                height={250}
              />
            ) : (
              <>
                {" "}
                <Box className="no-data-found">
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "18px",
                      color: "#7B8A8B",
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 400,
                      margin: "40px 0",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {seriesData?.length ? "Loading..." : " No data available"}
                  </p>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeadConversionRate;
